import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiSearch, FiUserPlus, FiCheck } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { searchPlayers } from '../actions/playersActions';
import EditPlayerModal from './EditPlayerModal';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/editModal.css';

// Добавление игрока в представители команды — строку related_teams.
// Пишем тем же рутом и тем же телом запроса, что и приложение
// (ingame_admin ManageTeams через connectPlayerToTeams):
// { playerId, add_data: [команда с player_number], remove_data: [] }.
//
// Это не заявка: заявка живёт в team_applications, а представители —
// в related_teams, и капитанство считается именно оттуда.

export default function AddRepresentativeModal({ isOpen, onClose, onSuccess, team, teamId }) {
    const dispatch = useDispatch();
    const userState = useSelector(state => state.user);
    const user = (userState && userState.userData && userState.userData.data) || {};

    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [playerNumber, setPlayerNumber] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setSearchQuery('');
        setResults([]);
        setHasSearched(false);
        setSelectedPlayer(null);
        setPlayerNumber('');
        setError(null);
    }, [isOpen]);

    if (!isOpen) return null;

    const runSearch = async () => {
        const query = searchQuery.trim();
        if (query.length < 2) {
            setError('Введите минимум 2 символа');
            return;
        }
        setError(null);
        setIsSearching(true);
        try {
            const parts = query.split(' ').filter(part => part.length > 0);
            const data = await dispatch(searchPlayers(
                API_URLS.searchPlayersByParams,
                { family: parts[0] || '', name: parts[1] || '', father_name: '' },
                0, 100,
            ));
            const rows = (data && data.data) || data || [];
            setResults(Array.isArray(rows) ? rows : []);
            setHasSearched(true);
        } catch (e) {
            console.error('Ошибка поиска игроков:', e);
            setError('Не удалось найти игроков');
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const save = async () => {
        if (!selectedPlayer) {
            setError('Выберите игрока');
            return;
        }
        setError(null);
        setIsSaving(true);
        try {
            if (checkIfTokenExpired()) {
                await dispatch(refreshTokens());
            }
            const tokens = getTokensFromStore();
            // Номер необязателен: player_number в related_teams допускает NULL
            const number = playerNumber === '' ? null : Number(playerNumber);
            await axios.post(
                BASE_URL.url + API_URLS.connectPlayerToTeam,
                {
                    playerId: selectedPlayer.id || selectedPlayer.player_id,
                    add_data: [{ ...(team || {}), id: Number(teamId), player_number: number }],
                    remove_data: [],
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + tokens.access_token,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                },
            );
            onSuccess();
        } catch (e) {
            console.error('Ошибка добавления представителя:', e);
            setError((e.response && e.response.data) || 'Не удалось добавить игрока');
        } finally {
            setIsSaving(false);
        }
    };

    const playerImage = (player) => {
        const image = player.image || player.player_image;
        if (!image) return defaultTeamLogo;
        return image.startsWith('http') ? image : BASE_URL.url + image;
    };

    return (
        <div className="modalOverlay" onClick={isSaving ? undefined : onClose}>
            <div className="modalContent modalContentWide" onClick={(e) => e.stopPropagation()}>
                <div className="modalHeader">
                    <h2>Добавить представителя</h2>
                    <button className="modalClose" disabled={isSaving} onClick={onClose}>x</button>
                </div>

                <div className="modalBody modalBodyScroll">
                    <p className="modalHint">
                        Представители — это состав команды в related_teams.
                        Отсюда игрок и становится капитаном на своей карточке.
                    </p>

                    <div className="repSearchRow">
                        <input
                            type="text"
                            className="modalInput"
                            value={searchQuery}
                            placeholder="Фамилия или фамилия и имя"
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => { if (e.key === 'Enter') runSearch(); }}
                            autoFocus
                        />
                        <button
                            className="modalButton modalButtonSave"
                            disabled={isSearching}
                            onClick={runSearch}
                        >
                            <FiSearch /> {isSearching ? 'Ищем...' : 'Искать'}
                        </button>
                    </div>

                    <button
                        type="button"
                        className="linkButton"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <FiUserPlus /> Игрока нет в базе — создать
                    </button>

                    {hasSearched && results.length === 0 && !isSearching ? (
                        <p className="modalHint">Игроки не найдены.</p>
                    ) : null}

                    {results.map(player => {
                        const id = player.id || player.player_id;
                        const picked = selectedPlayer
                            && (selectedPlayer.id || selectedPlayer.player_id) === id;
                        return (
                            <button
                                type="button"
                                key={id}
                                className={picked ? 'repRow repRowPicked' : 'repRow'}
                                onClick={() => setSelectedPlayer(player)}
                            >
                                <img
                                    src={playerImage(player)}
                                    alt=""
                                    onError={(e) => { e.target.src = defaultTeamLogo; }}
                                />
                                <span className="repRowMain">
                                    <span className="repRowName">
                                        {player.family} {player.name} {player.father_name || ''}
                                    </span>
                                    <span className="repRowMeta">{player.position || ''}</span>
                                </span>
                                {picked ? <FiCheck className="repRowCheck" /> : null}
                            </button>
                        );
                    })}

                    {selectedPlayer ? (
                        <div className="repNumberRow">
                            <label className="modalLabel">Игровой номер (необязательно)</label>
                            <input
                                type="number"
                                className="modalInput"
                                min={1}
                                max={99}
                                value={playerNumber}
                                onChange={(e) => setPlayerNumber(e.target.value)}
                            />
                        </div>
                    ) : null}

                    {error ? (
                        <span className="fieldError">
                            {typeof error === 'string' ? error : 'Не удалось добавить игрока'}
                        </span>
                    ) : null}
                </div>

                <div className="modalFooter">
                    <button className="modalButton modalButtonCancel" disabled={isSaving} onClick={onClose}>
                        Отмена
                    </button>
                    <button
                        className="modalButton modalButtonSave"
                        disabled={!selectedPlayer || isSaving}
                        onClick={save}
                    >
                        {isSaving ? 'Добавляем...' : 'Добавить в представители'}
                    </button>
                </div>
            </div>

            <EditPlayerModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={(newPlayer) => {
                    setShowCreateModal(false);
                    setSelectedPlayer(newPlayer);
                    setResults([newPlayer]);
                    setHasSearched(true);
                }}
                player={null}
                isCreateMode
                userData={user}
            />
        </div>
    );
}
